/** @format */

/**
 * Helper to get environment variables from runtime or build-time config
 */
const getEnvVar = (key, defaultValue = "") => {
  return (
    (window._env_ && window._env_[key]) || process.env[key] || defaultValue
  );
};

// API service for backend communication
const API_BASE = getEnvVar("REACT_APP_BACKEND_URL");

console.log("API Base URL:", API_BASE);

class ApiService {
  constructor() {
    this.baseUrl = API_BASE;
    this.getAccessToken = null; // Will be set by AuthContext
  }

  normalizeTimestamp(value) {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    // Numeric values from backend may be Unix seconds or milliseconds.
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      const absValue = Math.abs(numericValue);
      const timestampMs = absValue < 1e12 ? numericValue * 1000 : numericValue;
      const date = new Date(timestampMs);
      return Number.isNaN(date.getTime()) ? null : date.toISOString();
    }

    // String timestamps are expected to be ISO-compatible.
    const parsed = Date.parse(value);
    if (Number.isNaN(parsed)) {
      return null;
    }

    return new Date(parsed).toISOString();
  }

  normalizeChatTimestamps(chat) {
    if (!chat || typeof chat !== "object") return chat;

    return {
      ...chat,
      created_at: this.normalizeTimestamp(chat.created_at),
      updated_at: this.normalizeTimestamp(chat.updated_at),
    };
  }

  normalizeMessageTimestamps(message) {
    if (!message || typeof message !== "object") return message;

    return {
      ...message,
      created_at: this.normalizeTimestamp(message.created_at),
      updated_at: this.normalizeTimestamp(message.updated_at),
    };
  }

  /**
   * Set the access token getter function from AuthContext
   * @param {Function} getTokenFn - Function that returns a Promise<string>
   */
  setAccessTokenGetter(getTokenFn) {
    this.getAccessToken = getTokenFn;
  }

  /**
   * Get the Authorization header with Bearer token
   * @returns {Promise<Object>} Headers object with Authorization
   */
  async getAuthHeaders() {
    if (!this.getAccessToken) {
      console.error("❌ Access token getter not configured");
      throw new Error("Authentication not initialized");
    }

    try {
      const token = await this.getAccessToken();
      if (!token) {
        console.error("❌ No access token available");
        throw new Error("No access token available");
      }
      console.log("✅ Access token acquired for API request");
      return {
        Authorization: `Bearer ${token}`,
      };
    } catch (error) {
      console.error("❌ Failed to get access token:", error);
      throw error;
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      // Get auth headers
      const authHeaders = await this.getAuthHeaders();

      console.log(`🌐 API Request: ${options.method || "GET"} ${endpoint}`);

      const response = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
          ...options.headers,
        },
      });

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          console.error("❌ API returned 401 Unauthorized");
          const error = new Error("Authentication required");
          error.status = 401;
          throw error;
        }
        if (response.status === 403) {
          console.error("❌ API returned 403 Forbidden");
          const error = new Error("Access denied");
          error.status = 403;
          throw error;
        }

        const errorText = await response.text();
        console.error(`❌ API error ${response.status}:`, errorText);
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      if (response.status === 204) return null;

      const data = await response.json();
      console.log(`✅ API Response: ${options.method || "GET"} ${endpoint}`);
      return data;
    } catch (error) {
      console.error(
        `❌ API Request failed: ${options.method || "GET"} ${endpoint}`,
        error,
      );
      throw error;
    }
  }

  // ==================== User API ====================

  async loginUser(userData) {
    return this.request("/v1/auth/login", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  async getUser(userId) {
    return this.request(`/v1/users/${userId}`);
  }

  async getUserPreferences(userId) {
    return this.request(`/v1/users/${userId}/preferences`);
  }

  async updateUserPreferences(userId, preferences) {
    return this.request(`/v1/users/${userId}/preferences`, {
      method: "PUT",
      body: JSON.stringify(preferences),
    });
  }

  // ==================== Chat API ====================

  async createChat(userId, title = "New Chat") {
    const response = await this.request("/v1/chats", {
      method: "POST",
      body: JSON.stringify({ user_id: userId, title }),
    });
    return this.normalizeChatTimestamps(response);
  }

  async getUserChats(userId) {
    const response = await this.request(`/v1/chats?limit=100`);
    const chats = Array.isArray(response?.data) ? response.data : [];
    return chats.map((chat) => this.normalizeChatTimestamps(chat));
  }

  async getChat(chatId, userId) {
    const response = await this.request(`/v1/chats/${chatId}`);
    return this.normalizeChatTimestamps(response);
  }

  async updateChat(chatId, userId, title) {
    return this.request(`/v1/chats/${chatId}`, {
      method: "PUT",
      body: JSON.stringify({ title }),
    });
  }

  async deleteChat(chatId, userId) {
    return this.request(`/v1/chats/${chatId}`, {
      method: "DELETE",
    });
  }

  // ==================== Message API ====================

  async createMessage(chatId, userId, content, role = "user") {
    return this.request(`/v1/chat/completions`, {
      method: "POST",
      body: JSON.stringify({
        model: "rag-chatbot",
        messages: [{ role, content }],
        chat_id: chatId,
        stream: false,
      }),
    });
  }

  async getChatMessages(chatId, userId, limit = 100) {
    const response = await this.request(
      `/v1/chat/history/${chatId}?limit=${limit}&offset=0`,
    );
    if (Array.isArray(response)) {
      return response.map((message) => this.normalizeMessageTimestamps(message));
    }
    if (Array.isArray(response?.data)) {
      return response.data.map((message) => this.normalizeMessageTimestamps(message));
    }
    return [];
  }

  async updateMessageResponseTime(messageId, chatId, userId, responseTime) {
    // Note: Response time tracking is optional in V1 API
    // This is a best-effort update that may not be implemented
    try {
      return await this.request(
        `/v1/chat/history/${chatId}`,
        {
          method: "PUT",
          body: JSON.stringify({ message_id: messageId, response_time: responseTime }),
        }
      );
    } catch (error) {
      // Silently fail if endpoint not implemented - not critical functionality
      console.debug("Response time update not available:", error);
      return null;
    }
  }

  async getRecentMessages(chatId, userId, limit = 10) {
    // Uses the general message history endpoint with limit
    const response = await this.request(
      `/v1/chat/history/${chatId}?limit=${limit}&offset=0`,
    );
    if (Array.isArray(response)) {
      return response.map((message) => this.normalizeMessageTimestamps(message));
    }
    if (Array.isArray(response?.data)) {
      return response.data.map((message) => this.normalizeMessageTimestamps(message));
    }
    return [];
  }

  /**
   * Get AI response for a user message (streaming with OpenAI-compatible format)
   * @param {string} chatId - Chat ID
   * @param {string} userId - User ID
   * @param {string} message - User's message content
   * @param {function} onChunk - Callback function called for each chunk of streamed text
   * @returns {Promise<{reply: string, groundingScore?: number, sources?: Array}>} - AI's response
   */
  async getAIResponse(chatId, userId, message, onChunk = null) {
    if (chatId === undefined || chatId === null || chatId === "") {
      throw new Error("Missing chat id for completion request");
    }

    // Get auth headers
    const authHeaders = await this.getAuthHeaders();

    // OpenAI-compatible streaming request
    const response = await fetch(`${this.baseUrl}/v1/chat/completions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({
        model: "rag-chatbot",
        messages: [
          {
            role: "user",
            content: message,
          },
        ],
        chat_id: String(chatId),
        stream: true,
      }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        const error = new Error("Authentication required");
        error.status = 401;
        throw error;
      }
      if (response.status === 403) {
        const error = new Error("Access denied");
        error.status = 403;
        throw error;
      }

      const error = await response.text();
      throw new Error(error || `HTTP ${response.status}`);
    }

    // Handle OpenAI-format streaming response (Server-Sent Events)
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullResponse = "";
    let buffer = ""; // Buffer for incomplete lines
    let groundingInfo = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode the chunk
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Process complete lines from buffer
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          const line = buffer.slice(0, newlineIndex).trim();
          buffer = buffer.slice(newlineIndex + 1);

          // SSE format: "data: <json>"
          if (line.startsWith("data: ")) {
            const data = line.substring(6); // Remove "data: " prefix

            if (data === "[DONE]") {
              // Streaming is complete
              console.log("Stream complete");
              return {
                reply: fullResponse,
                groundingScore: groundingInfo?.x_grounding_score,
                supportedClaims: groundingInfo?.x_supported_claims,
                unsupportedClaims: groundingInfo?.x_unsupported_claims,
                sources: groundingInfo?.x_sources,
              };
            } else if (data.startsWith("{")) {
              // Parse OpenAI-format chunk
              try {
                const parsed = JSON.parse(data);
                
                // Handle error responses
                if (parsed.error) {
                  throw new Error(
                    parsed.error.message || "Streaming error from server"
                  );
                }

                // Extract content from delta
                if (
                  parsed.choices &&
                  parsed.choices[0] &&
                  parsed.choices[0].delta
                ) {
                  const delta = parsed.choices[0].delta;
                  if (delta.content) {
                    fullResponse += delta.content;
                    if (onChunk && typeof onChunk === "function") {
                      onChunk(delta.content);
                    }
                  }

                  // Capture grounding info from final chunk
                  if (parsed.choices[0].finish_reason === "stop") {
                    groundingInfo = {
                      x_grounding_score: parsed.x_grounding_score,
                      x_supported_claims: parsed.x_supported_claims,
                      x_unsupported_claims: parsed.x_unsupported_claims,
                      x_sources: parsed.x_sources,
                    };
                  }
                }
              } catch (e) {
                console.error("Failed to parse SSE chunk:", e);
                // Continue on parse errors
              }
            }
          }
        }
      }

      // If we finish without explicit [DONE] signal, return what we have
      return { reply: fullResponse };
    } catch (error) {
      reader.cancel();
      throw error;
    }
  }

  // ==================== Attachment API ====================

  async uploadAttachment(chatId, file, messageId = null) {
    // Get auth headers
    const authHeaders = await this.getAuthHeaders();

    const formData = new FormData();
    formData.append("file", file);

    let url = `${this.baseUrl}/v1/attachments?chat_id=${chatId}`;
    if (messageId) url += `&message_id=${messageId}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        ...authHeaders,
        // Don't set Content-Type for FormData - browser will set it with boundary
      },
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Authentication required");
      }
      if (response.status === 403) {
        throw new Error("Access denied");
      }
      throw new Error(`Upload failed: ${response.status}`);
    }

    return response.json();
  }

  async getChatAttachments(chatId) {
    return this.request(`/v1/attachments/chat/${chatId}`);
  }

  async downloadAttachment(attachmentId, chatId) {
    // Get auth headers
    const authHeaders = await this.getAuthHeaders();

    const response = await fetch(
      `${this.baseUrl}/v1/attachments/${attachmentId}/download?chat_id=${chatId}`,
      {
        headers: authHeaders,
      },
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Authentication required");
      }
      if (response.status === 403) {
        throw new Error("Access denied");
      }
      throw new Error("Download failed");
    }
    return response.blob();
  }

  async deleteAttachment(attachmentId, chatId) {
    return this.request(`/v1/attachments/${attachmentId}?chat_id=${chatId}`, {
      method: "DELETE",
    });
  }
}

export const api = new ApiService();
export default api;
